import axios from "../config/axios-kb-manager"

// Topics/documents/queries/graph client for the new Knowledge Manager backend, built from its
// OpenAPI spec (LightRAG-backed, scoped by organization/project/subProject — no per-topic
// server/host/port like the legacy KnowledgeDatabaseRepository backend).

const DEFAULT_CONFIG = {
    llmModel: "gpt-4o-mini",
    embeddingModel: "text-embedding-3-small",
    chunkSize: 1200,
    chunkOverlap: 100,
    maxTokens: 4096,
    temperature: 0.7,
    topK: 60,
    enableGraphSearch: true,
    graphCommunityLevel: 2,
};

function mapConfig(c) {
    if (!c) return {...DEFAULT_CONFIG};
    return {
        llmModel: c.llmModel ?? DEFAULT_CONFIG.llmModel,
        embeddingModel: c.embeddingModel ?? DEFAULT_CONFIG.embeddingModel,
        chunkSize: c.chunkSize ?? DEFAULT_CONFIG.chunkSize,
        chunkOverlap: c.chunkOverlap ?? DEFAULT_CONFIG.chunkOverlap,
        maxTokens: c.maxTokens ?? DEFAULT_CONFIG.maxTokens,
        temperature: c.temperature ?? DEFAULT_CONFIG.temperature,
        topK: c.topK ?? DEFAULT_CONFIG.topK,
        enableGraphSearch: c.enableGraphSearch ?? DEFAULT_CONFIG.enableGraphSearch,
        graphCommunityLevel: c.graphCommunityLevel ?? DEFAULT_CONFIG.graphCommunityLevel,
    };
}

function mapTopic(t) {
    return {
        id: t.id,
        name: t.name,
        description: t.description,
        tags: t.tags || [],
        color: t.color,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
        createdBy: t.createdBy,
        documentCount: t.documentCount || 0,
        queryCount: t.queryCount || 0,
        config: mapConfig(t.config),
        ragTenantId: t.ragTenantId,
        projectId: t.projectId,
        subProjectId: t.subProjectId,
    };
}

export function mapDocument(d) {
    return {
        id: d.id,
        topicId: d.topicId,
        name: d.name,
        type: d.mimeType,
        size: d.sizeBytes,
        status: d.status,
        chunks: d.chunks,
        uploadedAt: d.uploadedAt,
        uploadedBy: d.uploadedBy,
        errorMessage: d.errorMessage ?? undefined,
    };
}

function mapQuery(q) {
    return {
        id: q.id,
        topicId: q.topicId,
        query: q.query,
        response: q.response,
        mode: q.mode,
        sources: q.sources || [],
        tokensUsed: q.tokensUsed,
        processingTimeMs: q.processingTimeMs,
        llmModel: q.llmModel,
        embeddingModel: q.embeddingModel,
        topK: q.topK,
        timestamp: q.createdAt,
        createdBy: q.createdBy,
    };
}

function mapDocumentContent(c) {
    return {
        documentId: c.documentId,
        mimeType: c.mimeType,
        content: c.content,
        characterCount: c.characterCount,
    };
}

function normalizeNodeType(rawType) {
    const value = rawType?.toLowerCase();
    if (value === "relation") return "relation";
    if (value === "chunk") return "chunk";
    return "entity";
}

function mapNode(node, fallbackIndex) {
    const id = String(node.id ?? node.label ?? node.name ?? `node-${fallbackIndex}`);
    const label = String(node.label ?? node.name ?? node.id ?? `Node ${fallbackIndex + 1}`);
    const type = normalizeNodeType(node.type ?? node.entity_type ?? node.nodeType ?? node.kind);
    const properties = node.properties ?? node.data ?? {};
    return {id, label, type, properties};
}

function mapEdge(edge, fallbackIndex) {
    return {
        source: String(edge.source ?? edge.source_id ?? edge.from ?? ""),
        target: String(edge.target ?? edge.target_id ?? edge.to ?? ""),
        label: String(edge.label ?? edge.relation ?? edge.type ?? `edge-${fallbackIndex + 1}`),
        weight: edge.weight,
    };
}

function mapGraphResponse(raw) {
    const nodes = (raw.nodes ?? []).map(mapNode);
    const edges = (raw.edges ?? []).map(mapEdge);
    return {
        nodes,
        edges,
        totalEntities: typeof raw.totalEntities === "number" ? raw.totalEntities : raw.total_entities,
        totalRelationships: typeof raw.totalRelationships === "number" ? raw.totalRelationships : raw.total_relationships,
        raw,
    };
}

function basePath(topicId) {
    return `/api/topics/${topicId}`;
}

function cleanParams(params) {
    const result = {};
    for (const [key, value] of Object.entries(params || {})) {
        if (value === undefined || value === null || value === "") continue;
        result[key] = value;
    }
    return result;
}

export const KnowledgeManagerRepository = {

    // Topics
    async listTopics({organizationId, projectId, subProjectId} = {}) {
        const raw = await axios.get("/api/topics", {params: cleanParams({organizationId, projectId, subProjectId})});
        return raw.data.map(mapTopic);
    },
    async getTopic(id) {
        const raw = await axios.get(`/api/topics/${id}`);
        return mapTopic(raw.data);
    },
    async createTopic(req) {
        const raw = await axios.post("/api/topics", req);
        return mapTopic(raw.data);
    },
    async patchTopic(id, req) {
        const raw = await axios.patch(`/api/topics/${id}`, req);
        return mapTopic(raw.data);
    },
    async deleteTopic(id) {
        return axios.delete(`/api/topics/${id}`);
    },
    async getConfig(topicId) {
        const raw = await axios.get(`${basePath(topicId)}/config`);
        return mapConfig(raw.data);
    },
    async upsertConfig(topicId, req) {
        const raw = await axios.put(`${basePath(topicId)}/config`, req);
        return mapConfig(raw.data);
    },

    // Documents
    async listDocuments(topicId, {organizationId, projectId, subProjectId} = {}) {
        const raw = await axios.get(`${basePath(topicId)}/documents`, {params: cleanParams({organizationId, projectId, subProjectId})});
        return raw.data.map(mapDocument);
    },
    async searchDocumentsByContent(topicId, content, {limit, organizationId, projectId, subProjectId} = {}) {
        const raw = await axios.get(`/api/documents/${topicId}/search`, {params: cleanParams({content, limit, organizationId, projectId, subProjectId})});
        return raw.data;
    },
    async uploadDocument(topicId, file, onProgress) {
        const formData = new FormData();
        formData.append("file", file);
        const raw = await axios.post(`${basePath(topicId)}/documents`, formData, {
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) onProgress?.(Math.round((progressEvent.loaded / progressEvent.total) * 100));
            },
        });
        return mapDocument(raw.data);
    },
    async uploadTextDocument(topicId, {content, fileName}) {
        const raw = await axios.post(`/api/documents/${topicId}/uploadText`, {content, fileName});
        return mapDocument(raw.data);
    },
    async updateDocument(docId, file, onProgress) {
        const formData = new FormData();
        formData.append("file", file);
        const raw = await axios.put(`/api/documents/${docId}`, formData, {
            onUploadProgress: (progressEvent) => {
                if (progressEvent.total) onProgress?.(Math.round((progressEvent.loaded / progressEvent.total) * 100));
            },
        });
        return mapDocument(raw.data);
    },
    async updateDocumentText(docId, {content, fileName}) {
        const raw = await axios.put(`/api/documents/${docId}/text`, {content, fileName});
        return mapDocument(raw.data);
    },
    async deleteDocument(docId) {
        return axios.delete(`/api/documents/${docId}`);
    },
    async getDocumentContent(docId) {
        const raw = await axios.get(`/api/documents/${docId}/content`);
        return mapDocumentContent(raw.data);
    },

    // Queries
    async listQueries(topicId, {limit = 50, organizationId, projectId, subProjectId} = {}) {
        const raw = await axios.get(`${basePath(topicId)}/queries`, {params: cleanParams({limit, organizationId, projectId, subProjectId})});
        return raw.data.map(mapQuery);
    },
    async executeQuery(topicId, req) {
        const raw = await axios.post(`${basePath(topicId)}/queries`, req);
        return mapQuery(raw.data);
    },

    // Knowledge graph
    async fetchGraph(topicId, {label = "*", maxDepth, maxNodes} = {}) {
        const raw = await axios.get(`${basePath(topicId)}/graph`, {params: cleanParams({label, maxDepth, maxNodes})});
        return mapGraphResponse(raw.data);
    },
    async listGraphLabels(topicId) {
        const raw = await axios.get(`${basePath(topicId)}/graph/label/list`);
        return raw.data;
    },
    async listPopularGraphLabels(topicId, limit) {
        const raw = await axios.get(`${basePath(topicId)}/graph/label/popular`, {params: cleanParams({limit})});
        return raw.data;
    },
    async searchGraphLabels(topicId, q, limit) {
        const raw = await axios.get(`${basePath(topicId)}/graph/label/search`, {params: cleanParams({q, limit})});
        return raw.data;
    },
    async entityExists(topicId, name) {
        const raw = await axios.get(`${basePath(topicId)}/graph/entity/exists`, {params: cleanParams({name})});
        return raw.data;
    },
    async createEntity(topicId, body) {
        const raw = await axios.post(`${basePath(topicId)}/graph/entity/create`, body);
        return raw.data;
    },
    async updateEntity(topicId, body) {
        const raw = await axios.post(`${basePath(topicId)}/graph/entity/edit`, body);
        return raw.data;
    },
    async createRelation(topicId, body) {
        const raw = await axios.post(`${basePath(topicId)}/graph/relation/create`, body);
        return raw.data;
    },
    async updateRelation(topicId, body) {
        const raw = await axios.post(`${basePath(topicId)}/graph/relation/edit`, body);
        return raw.data;
    },
    async mergeEntities(topicId, body) {
        const raw = await axios.post(`${basePath(topicId)}/graph/entities/merge`, body);
        return raw.data;
    },

    // Auth (the Knowledge Manager backend's own JWT — separate from the OpenBrain user session)
    async register(req) {
        const raw = await axios.post("/api/auth/register", req);
        return raw.data;
    },
    async login(req) {
        const raw = await axios.post("/api/auth/login", req);
        return raw.data;
    },
    async me() {
        const raw = await axios.get("/api/auth/me");
        return raw.data;
    },
};
